import { useEffect, useState } from 'react';
import { Spinner, Stack } from 'react-bootstrap';
import { FaArrowLeft } from 'react-icons/fa6';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { DetailSection, LabelText } from '../../../../../components/generic';
import ConfirmationModal from '../../../../../components/modals/ConfirmationModal';
import { LinkCell } from '../../../../../components/tables/cellComponents/LinkCell';
import { useToast } from '../../../../../context/ToastContext';
import {
  useDeleteAllergyMutation,
  useGetAllergyQuery
} from '../../../../../services/endpoints/people/allergy';
import { Allergy } from '../../../../../services/endpoints/people/user';
import { dateToUTC } from '../../../../../utils/dateUtils';
import { getErrorMessage } from '../../../../../utils/utils';
import { Forbidden } from '../../../../../components/error/Forbidden';
import { getCurrentUser } from '../../../../../services/helper';

export const AllergyDetails = () => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;
  const currentUser = getCurrentUser();
  const { addToast } = useToast();
  const { data, error, isLoading } = useGetAllergyQuery(+params.id!);
  const [allergy, setAllergy] = useState<Allergy>();
  const [deleteAllergy, { isLoading: isDeleting }] = useDeleteAllergyMutation();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (error && !isLoading) {
      addToast('Error while loading allergy', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
    if (data) {
      setAllergy(data.allergy);
    }
  }, [data]);

  const handleDelete = async () => {
    try {
      await deleteAllergy(+params.id!).unwrap();
      addToast('Allergy deleted successfully', 'success');
      navigate('/admin/allergies');
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  if (error && 'status' in error && error.status === 403) {
    return <Forbidden />;
  }

  if (isLoading || !allergy) {
    return (
      <div className="w-100 vh-100 d-inline-flex">
        <Spinner
          variant="primary"
          className="m-auto"
        />
      </div>
    );
  }

  return (
    <>
      <div
        className="d-flex justify-content-between align-items-center px-lg-5 border-bottom px-4 py-3"
        style={{ background: '#EFEFEF' }}
      >
        <Stack
          direction="horizontal"
          gap={2}
          className="ps-lg-3 ps-0"
        >
          <button
            className="btn btn-link text-info p-0"
            onClick={() => navigate(returnTo ?? '/admin/allergies')}
          >
            <FaArrowLeft
              size={20}
              className="d-lg-none"
            />
            <FaArrowLeft
              size={24}
              className="d-none d-lg-block"
            />
          </button>
          <h2
            className="d-lg-none text-secondary fw-semibold text-uppercase mb-0 pt-1"
            style={{ color: '#888888', fontSize: '1rem' }}
          >
            Allergies List
          </h2>
          <h2 className="d-none d-lg-block text-info fw-bold mb-0">{`Allergy Details`}</h2>
        </Stack>
        {!currentUser?.isInstructor && (
            <Stack
                direction="horizontal"
                gap={1}
                className="d-none d-lg-flex"
            >
              <SimpleButton
                  variant="primary"
                  height={36}
                  onClick={() => navigate(`/admin/allergies/${allergy?.id}/edit`)}
              >
                Edit Allergy
              </SimpleButton>
              <SimpleButton
                  variant="primary"
                  height={36}
                  onClick={() => setShowModal(true)}
              >
                Delete Allergy
              </SimpleButton>
            </Stack>
        )}
      </div>
      <div>
        <Stack
          gap={4}
          className="p-4"
        >
          <DetailSection>
            <LabelText label="Child:">
              <LinkCell
                content={allergy?.child?.full_name}
                url={`/admin/sfc-users/children/${allergy?.child?.id}`}
              />
            </LabelText>
            <LabelText
              label="Allergy:"
              text={allergy?.allergy}
            />
            <LabelText
              label="Severity:"
              text={allergy?.severity}
            />
            <LabelText
              label="Details:"
              text={allergy?.details}
            />
            <LabelText
              label="Created At:"
              text={
                allergy?.created_at ? dateToUTC(new Date(allergy?.created_at), 'MM/dd/yyyy') : 'N/A'
              }
            />
            <LabelText
              label="Updated At:"
              text={
                allergy?.updated_at ? dateToUTC(new Date(allergy?.updated_at), 'MM/dd/yyyy') : 'N/A'
              }
            />
          </DetailSection>
        </Stack>
      </div>
      <ConfirmationModal
        show={showModal}
        title="Delete Allergy"
        message="Are you sure you want to delete this allergy?"
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowModal(false)}
        isLoading={isDeleting}
      />
    </>
  );
};
